import { authApi } from '../api/auth';
import secureLocalStorage from 'react-secure-storage';
import { logInUser } from '../../features/userSlice';
import businessService from './businessService';

class AuthService {
  /**
   * Handle user login
   */
  async login(email, password, dispatch, navigate, location, cart) {
    try {
      // Ensure businessId is set before login
      const businessId = await businessService.getBusinessId();
      if (!businessId) {
        return {
          success: false,
          message: 'Unable to identify business. Please try again.'
        };
      }

      // Call login API; retry once — the first hit after a cold start can
      // time out or return a gateway 5xx while the backend warms up
      let response = await authApi.login(email, password);
      if (response.status >= 500) {
        response = await authApi.login(email, password);
      }

      if (response.status === 200) {
        let data = response.response;

        // Forced pin->password migration: the account has no password yet, so
        // there is no session — the user must set one with the restricted
        // token before logging in again.
        if (data?.passwordSetupRequired) {
          return {
            success: false,
            setupRequired: true,
            setupToken: data.token,
            reason: data.reason,
            message: 'Set a new password to continue',
          };
        }

        // Get refreshed token if needed
        const refreshedToken = await authApi.getNewTokenAfterLogin(data?.branchId, data?.token);
        if (refreshedToken?.data?.token) {
          data.token = refreshedToken.data.token;
        }

        // Store user data
        this.storeUserData(data);

        // Update Redux state
        if (dispatch) {
          dispatch(logInUser({ isLoggedIn: true }));
        }

        // Navigate based on user group
        this.handlePostLoginNavigation(data?.group, navigate, location, cart);

        return {
          success: true,
          message: 'Login Successful',
          data: data
        };
      } else {
        // Handle different error codes
        return this.handleLoginError(response.status);
      }
    } catch (error) {
      console.error('Login service error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      };
    }
  }

  /**
   * Complete the forced password setup, then the caller re-logs-in with the
   * new password. The stored token is cleared first: HttpRequest prefers a
   * stored token over passed headers, and a stale one would shadow the
   * restricted setup token.
   */
  async setupPassword(setupToken, newPassword) {
    try {
      secureLocalStorage.removeItem('token');
      const response = await authApi.setupPassword(setupToken, newPassword);
      if (response.status === 200) {
        return { success: true, message: 'Password set. Logging you in…' };
      }
      if (response.status === 401 || response.status === 403) {
        return { success: false, expired: true, message: 'This session has expired. Please log in again.' };
      }
      return { success: false, message: response?.response?.message || 'Could not set password' };
    } catch (error) {
      console.error('Password setup error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  }

  /**
   * Store user data in secure storage
   */
  storeUserData(data) {
    secureLocalStorage.setItem("token", data?.token);
    secureLocalStorage.setItem("group", data?.group);
    secureLocalStorage.setItem("zedAccountingId", data?.zedAccountingId);
    secureLocalStorage.setItem("email", data?.email);
    secureLocalStorage.setItem("state", data?.state);
    secureLocalStorage.setItem("username", data?.username);
    secureLocalStorage.setItem("businessName", data?.businessName);
    secureLocalStorage.setItem("userId", data?.userId);
    secureLocalStorage.setItem("BranchID", data?.branchId);
    secureLocalStorage.setItem("customerId", data?.customerId);
    secureLocalStorage.setItem("businessCategory", data?.businessCategory);
    secureLocalStorage.setItem('localCurrency', data?.localCurrency);
    secureLocalStorage.setItem('customerClassification', data?.customerClassification);
    secureLocalStorage.setItem("affiliatedPartner", data?.partnerName);
  }

  /**
   * Handle navigation after successful login
   */
  handlePostLoginNavigation(userGroup, navigate, location, cart) {
    if (!navigate) return;

    switch (userGroup) {
      case 'Customer':
        if (cart?.length > 0) {
          navigate('/checkout');
        } else if (location?.state?.previousPath === '/evouchers') {
          navigate('/evouchers');
        } else {
          navigate('/');
        }
        break;
      case 'merchant':
      case 'Accountant':
      case 'admin':
        navigate('/zed_ecommerce/dashboard');
        break;
      default:
        // Stay on current page if user group is not recognized
        break;
    }
  }

  /**
   * Handle login errors based on status code
   */
  handleLoginError(status) {
    switch (status) {
      case 400:
        return {
          success: false,
          message: 'User not found, please provide correct email and password'
        };
      case 505:
        return {
          success: false,
          message: 'Invalid Password'
        };
      case 507:
        return {
          success: false,
          message: 'Account Suspended for Numerous Login Attempts'
        };
      case 405:
        return {
          success: false,
          message: 'User Does not Exist!'
        };
      default:
        return {
          success: false,
          message: 'An error occurred during login. Please try again.'
        };
    }
  }

  /**
   * Handle user registration
   */
  async register(userData, dispatch, navigate, cart) {
    try {
      // Ensure businessId is set before registration
      const businessId = await businessService.getBusinessId();
      if (!businessId) {
        return {
          success: false,
          message: 'Unable to identify business. Please try again.'
        };
      }

      const response = await authApi.register(userData);

      if (response?.response?.Status === "Success") {
        // Store user data from registration response
        const responseData = response?.response?.data?.data;

        if (responseData) {
          secureLocalStorage.setItem("token", response?.response?.token);
          secureLocalStorage.setItem("group", "Customer");
          secureLocalStorage.setItem("zedAccountingId", responseData?.zedAccountingId);
          secureLocalStorage.setItem("email", responseData?.email);
          secureLocalStorage.setItem("state", responseData?.state);
          secureLocalStorage.setItem("username", responseData?.lastName);
          secureLocalStorage.setItem("businessName", responseData?.businessName);
          secureLocalStorage.setItem("userId", responseData?.userId);
          secureLocalStorage.setItem("BranchID", responseData?.branchId);
          secureLocalStorage.setItem("customerId", responseData?._id);
          secureLocalStorage.setItem("businessCategory", responseData?.businessCategory);
          secureLocalStorage.setItem('localCurrency', responseData?.localCurrency);
          secureLocalStorage.setItem('customerClassification', responseData?.customerClassification);
          secureLocalStorage.setItem("affiliatedPartner", responseData?.partnerName);

          // Update Redux state
          if (dispatch) {
            dispatch(logInUser({ isLoggedIn: true }));
          }

          // Navigate after successful registration
          if (navigate) {
            setTimeout(() => {
              if (cart?.length > 0) {
                navigate('/checkout');
              } else {
                navigate('/');
              }
            }, 2000);
          }
        }

        return {
          success: true,
          message: 'Registration Successful! Redirecting...',
          data: responseData
        };
      } else {
        return {
          success: false,
          message: response?.response?.message || 'Registration failed. Please try again.'
        };
      }
    } catch (error) {
      console.error('Registration service error:', error);
      return {
        success: false,
        message: 'Registration failed. Please try again.'
      };
    }
  }

  /**
   * Handle forgot password
   */
  async requestPasswordReset(email) {
    try {
      const response = await authApi.requestPasswordReset(email);

      if (response.status === 200) {
        return {
          success: true,
          message: 'Password reset code sent to your email'
        };
      } else {
        return {
          success: false,
          message: response.message || 'Failed to send reset code'
        };
      }
    } catch (error) {
      console.error('Password reset service error:', error);
      return {
        success: false,
        message: 'An error occurred. Please try again.'
      };
    }
  }

  /**
   * Verify password reset code
   */
  async verifyResetCode(email, code) {
    try {
      const response = await authApi.verifyResetCode(email, code);

      if (response.status === 200) {
        return {
          success: true,
          message: 'Code verified successfully'
        };
      } else {
        return {
          success: false,
          message: 'Invalid or expired code'
        };
      }
    } catch (error) {
      console.error('Verify code service error:', error);
      return {
        success: false,
        message: 'An error occurred. Please try again.'
      };
    }
  }

  /**
   * Reset password
   */
  async resetPassword(email, code, newPassword) {
    try {
      const response = await authApi.resetPassword(email, code, newPassword);

      if (response.status === 200) {
        return {
          success: true,
          message: 'Password reset successfully. Please login with your new password.'
        };
      } else {
        return {
          success: false,
          message: response.message || 'Failed to reset password'
        };
      }
    } catch (error) {
      console.error('Reset password service error:', error);
      return {
        success: false,
        message: 'An error occurred. Please try again.'
      };
    }
  }

  /**
   * Logout user
   */
  async logout(dispatch) {
    try {
      await authApi.logout();

      // Update Redux state
      if (dispatch) {
        dispatch(logInUser({ isLoggedIn: false }));
      }

      return {
        success: true,
        message: 'Logged out successfully'
      };
    } catch (error) {
      console.error('Logout service error:', error);
      return {
        success: false,
        message: 'Error during logout'
      };
    }
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn() {
    const token = secureLocalStorage.getItem('token');
    const userId = secureLocalStorage.getItem('userId');
    return !!(token && userId);
  }

  /**
   * Get current user data
   */
  getCurrentUser() {
    if (!this.isLoggedIn()) {
      return null;
    }

    return {
      email: secureLocalStorage.getItem('email'),
      username: secureLocalStorage.getItem('username'),
      userId: secureLocalStorage.getItem('userId'),
      group: secureLocalStorage.getItem('group'),
      businessName: secureLocalStorage.getItem('businessName'),
      customerId: secureLocalStorage.getItem('customerId')
    };
  }

  /**
   * Validate email format
   */
  validateEmail(email) {
    const trimmedEmail = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(trimmedEmail);
  }

  /**
   * Validate password strength
   */
  validatePassword(password) {
    // At least 6 characters
    if (password.length < 6) {
      return {
        valid: false,
        message: 'Password must be at least 6 characters long'
      };
    }

    return {
      valid: true,
      message: ''
    };
  }
}

const authService = new AuthService();
export default authService;